import React, { useEffect, useMemo, useState } from 'react';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Table from '@cloudscape-design/components/table';
import TextFilter from '@cloudscape-design/components/text-filter';
import Tabs from '@cloudscape-design/components/tabs';
import Badge from '@cloudscape-design/components/badge';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import SegmentedControl from '@cloudscape-design/components/segmented-control';
import Select from '@cloudscape-design/components/select';
import Alert from '@cloudscape-design/components/alert';
import Modal from '@cloudscape-design/components/modal';
import Spinner from '@cloudscape-design/components/spinner';
import RadioGroup from '@cloudscape-design/components/radio-group';
import FormField from '@cloudscape-design/components/form-field';
import Avatar from '../components/Avatar';
import { getSupplierControlService } from '../services';
import type {
    ControlSite,
    ControlRequest,
    ControlUser,
    OnboardedSupplier,
    SupplierDetail,
    SupplierOption,
    SupplierGroupOption,
    SupplierEntity,
    SupplierAssignment,
    ControlMemberRequest,
    UserType,
} from '../services/types';

// Supplier Control — internal access management. Ported from the dscs
// prototypes (Suppliers directory + Approval Queue + Supplier Review), wired to
// the SupplierControlService. Data is empty until the backend is built.

type ControlView = 'suppliers' | 'queue' | 'member-requests' | 'review' | 'users';

function fmtDate(iso: string): string {
    if (!iso) return '—';
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString();
}

/** A name + email cell with an initials avatar, shared by the tables. */
function PartyCell({ name, email, seed }: { name: string; email: string; seed: string }) {
    return (
        <SpaceBetween size="xs" direction="horizontal" alignItems="center">
            <Avatar name={name} seed={seed} />
            <SpaceBetween size="xxxs">
                <Box fontWeight="bold">{name}</Box>
                <Box color="text-body-secondary" fontSize="body-s">
                    {email}
                </Box>
            </SpaceBetween>
        </SpaceBetween>
    );
}

// ── Brand drill-down (master -> members -> per-member entity access) ──────────
function SupplierDetailModal({
    admin,
    onClose,
}: {
    admin: OnboardedSupplier;
    onClose: () => void;
}) {
    const [detail, setDetail] = useState<SupplierDetail | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let active = true;
        setDetail(null);
        setError(null);
        if (!admin.supplierId) {
            setError('This supplier admin has no supplier assigned.');
            return undefined;
        }
        getSupplierControlService()
            .getSupplierDetail(admin.supplierId)
            .then((d) => active && setDetail(d))
            .catch((e) => active && setError(e instanceof Error ? e.message : 'Load failed.'));
        return () => {
            active = false;
        };
    }, [admin.supplierId]);

    return (
        <Modal visible onDismiss={onClose} header={`Supplier users · ${admin.company}`} size="large">
            {error ? <Alert type="error">{error}</Alert> : null}
            {!error && detail === null ? <Spinner /> : null}
            {detail ? (
                <SpaceBetween size="l">
                    <Box color="text-body-secondary">
                        {detail.members.length} user(s) · {detail.entityCount} legal entities. Every
                        user in this supplier has access to all of these entities.
                    </Box>

                    {/* The group's shared legal-entity set (every user inherits it). */}
                    <Table
                        variant="embedded"
                        items={detail.entities ?? []}
                        header={
                            <Header counter={`(${detail.entityCount})`} variant="h3">
                                Legal entities
                            </Header>
                        }
                        empty={
                            <Box textAlign="center" color="text-body-secondary" padding="s">
                                No legal entities assigned to this supplier yet.
                            </Box>
                        }
                        columnDefinitions={[
                            {
                                id: 'supplierNo',
                                header: 'SupplierNo',
                                cell: (e) => <Box fontWeight="bold">{e.supplierNo}</Box>,
                            },
                            { id: 'label', header: 'Legal entity', cell: (e) => e.displayLabel },
                        ]}
                    />

                    {/* The group's users (roster only — access is group-level). */}
                    <Table
                        variant="embedded"
                        items={detail.members}
                        header={
                            <Header counter={`(${detail.members.length})`} variant="h3">
                                Users
                            </Header>
                        }
                        empty={
                            <Box textAlign="center" color="text-body-secondary" padding="s">
                                No users yet.
                            </Box>
                        }
                        columnDefinitions={[
                            {
                                id: 'email',
                                header: 'User',
                                cell: (m) => m.email || m.username,
                            },
                            {
                                id: 'role',
                                header: 'Role',
                                cell: (m) => (
                                    <Badge color={m.role === 'MASTER' ? 'green' : 'grey'}>
                                        {m.role === 'MASTER' ? 'Admin' : 'User'}
                                    </Badge>
                                ),
                            },
                            {
                                id: 'status',
                                header: 'Status',
                                cell: (m) => (
                                    <StatusIndicator type={m.enabled ? 'success' : 'stopped'}>
                                        {m.enabled ? 'Active' : 'Disabled'}
                                    </StatusIndicator>
                                ),
                            },
                        ]}
                    />
                </SpaceBetween>
            ) : null}
        </Modal>
    );
}

// ── Onboarded suppliers directory (brand masters) ─────────────────────────────
function SuppliersView({ suppliers }: { suppliers: OnboardedSupplier[] }) {
    const [q, setQ] = useState('');
    const [drillInto, setDrillInto] = useState<OnboardedSupplier | null>(null);
    const query = q.trim().toLowerCase();
    const shown = suppliers.filter(
        (s) =>
            query === '' ||
            s.company.toLowerCase().includes(query) ||
            s.email.toLowerCase().includes(query),
    );

    return (
        <>
            <Table
                variant="container"
                items={shown}
                filter={
                    <TextFilter
                        filteringText={q}
                        filteringPlaceholder="Search by supplier or email…"
                        onChange={({ detail }) => setQ(detail.filteringText)}
                    />
                }
                header={
                    <Header
                        counter={`(${suppliers.length})`}
                        description="Each row is a supplier's admin. Open one to see its members and their legal-entity access."
                    >
                        Onboarded suppliers
                    </Header>
                }
                empty={
                    <Box textAlign="center" color="text-body-secondary" padding="l">
                        {suppliers.length === 0
                            ? 'No onboarded suppliers yet. Approve sign-ups from the Approval Queue.'
                            : 'No matching suppliers.'}
                    </Box>
                }
                columnDefinitions={[
                    {
                        id: 'supplier',
                        header: 'Supplier admin',
                        cell: (s) => <PartyCell name={s.company} email={s.email} seed={s.id} />,
                    },
                    {
                        id: 'status',
                        header: 'Status',
                        cell: () => <StatusIndicator type="success">Active</StatusIndicator>,
                    },
                    {
                        id: 'onboarded',
                        header: 'Onboarded',
                        cell: (s) => fmtDate(s.onboarded),
                    },
                    {
                        id: 'actions',
                        header: '',
                        cell: (s) => (
                            <Button
                                variant="normal"
                                disabled={!s.supplierId}
                                onClick={() => setDrillInto(s)}
                            >
                                View access
                            </Button>
                        ),
                    },
                ]}
            />
            {drillInto ? (
                <SupplierDetailModal admin={drillInto} onClose={() => setDrillInto(null)} />
            ) : null}
        </>
    );
}

// ── Team picker (assign-then-approve) ─────────────────────────────────────────
// Shown when the admin approves a pending request. Lists the teams that already
// exist for the login's email domain — each with a few member emails so the
// admin can recognize colleagues — and lets them pick one or create a new
// supplier, then approve. The resolved assignment (existing supplierId or new
// supplierName) is handed back to the caller, which runs the approval.
// Sentinel option in the group dropdown for "create a new group under this supplier".
const NEW_GROUP = '__new_group__';

// Brand-based approval picker: the admin selects the brand this login belongs
// to (suggested from the email as a clue), sees that brand's legal entities,
// chooses the login's role, then approves. Replaces the old domain-scoped team
// picker — brand, not email domain, is the grouping decision.
function SupplierPickerModal({
    headerText,
    confirmLabel,
    email,
    onCancel,
    onConfirm,
    submitting,
}: {
    headerText: string;
    confirmLabel: string;
    email: string;
    onCancel: () => void;
    onConfirm: (assignment: SupplierAssignment) => void;
    submitting: boolean;
}) {
    // Step 1: pick a parent SUPPLIER (top-level only). Step 2: pick an existing
    // GROUP under it, or create a new one.
    const [suppliers, setSuppliers] = useState<SupplierOption[] | null>(null);
    const [suggestedId, setSuggestedId] = useState<string | null>(null);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [selectedParent, setSelectedParent] = useState<string>('');

    // Groups under the selected parent, and the chosen group (an existing group
    // id, or NEW_GROUP to create one).
    const [groups, setGroups] = useState<SupplierGroupOption[] | null>(null);
    const [selectedGroup, setSelectedGroup] = useState<string>('');
    // Role when joining an EXISTING group: Admin (a second master) or User.
    // Ignored for a new group (its owning login is always the master).
    const [existingRole, setExistingRole] = useState<'MASTER' | 'MEMBER'>('MEMBER');

    // Entities for the current selection. For NEW_GROUP they are the parent's
    // catalog, checkable (the chosen subset becomes the new group's set). For an
    // existing group they are that group's assigned set, shown READ-ONLY.
    const [entities, setEntities] = useState<SupplierEntity[] | null>(null);
    const [checkedNos, setCheckedNos] = useState<Record<string, boolean>>({});

    const isNewGroup = selectedGroup === NEW_GROUP;

    // Load the parent suppliers (top-level only — exclude group rows).
    useEffect(() => {
        let active = true;
        setSuppliers(null);
        setLoadError(null);
        getSupplierControlService()
            .listSuppliersForPicker(email)
            .then((res) => {
                if (!active) return;
                const parents = res.suppliers.filter((s) => !s.parentSupplierId);
                setSuppliers(parents);
                setSuggestedId(res.suggestedSupplierId);
                const suggestedIsParent = parents.some(
                    (p) => p.supplierId === res.suggestedSupplierId,
                );
                if (res.suggestedSupplierId && suggestedIsParent)
                    setSelectedParent(res.suggestedSupplierId);
                else if (parents.length > 0) setSelectedParent(parents[0].supplierId);
            })
            .catch((e) => active && setLoadError(e instanceof Error ? e.message : 'Load failed.'));
        return () => {
            active = false;
        };
    }, [email]);

    // When the parent changes, load its existing groups and default to creating
    // a new group (the common case for a brand-new master).
    useEffect(() => {
        let active = true;
        setGroups(null);
        setSelectedGroup(NEW_GROUP);
        setExistingRole('MEMBER');
        if (!selectedParent) return undefined;
        getSupplierControlService()
            .listGroupsForParent(selectedParent)
            .then((g) => active && setGroups(g))
            .catch(() => active && setGroups([]));
        return () => {
            active = false;
        };
    }, [selectedParent]);

    // Load entities for the current group selection:
    //  - NEW_GROUP  -> the PARENT's catalog, checkable (defaults all selected).
    //  - a group id -> that group's assigned set, read-only (all shown as-is).
    useEffect(() => {
        let active = true;
        setEntities(null);
        setCheckedNos({});
        const sourceId = isNewGroup ? selectedParent : selectedGroup;
        if (!sourceId) return undefined;
        getSupplierControlService()
            .getSupplierEntities(sourceId)
            .then((e) => {
                if (!active) return;
                setEntities(e);
                if (isNewGroup) {
                    // Default all of the parent's entities selected for a new group.
                    setCheckedNos(Object.fromEntries(e.map((x) => [x.supplierNo, true])));
                }
            })
            .catch(() => active && setEntities([]));
        return () => {
            active = false;
        };
    }, [selectedParent, selectedGroup, isNewGroup]);

    const checkedList = () =>
        (entities ?? []).map((e) => e.supplierNo).filter((n) => checkedNos[n]);

    const confirm = () => {
        if (isNewGroup) {
            // Create a NEW auto-named group under the selected parent and make
            // this login its MASTER, with the chosen entity subset.
            onConfirm({
                newGroup: true,
                parentSupplierId: selectedParent,
                role: 'MASTER',
                entitySupplierNos: checkedList(),
            });
        } else {
            // Join the selected EXISTING group. ADMIN adds a second master;
            // USER adds a member. Either way the login inherits the group's
            // existing entity set (never re-set here).
            onConfirm({ supplierId: selectedGroup, role: existingRole });
        }
    };

    const selectedParentSupplier = (suppliers ?? []).find(
        (b) => b.supplierId === selectedParent,
    );

    const canConfirm = Boolean(selectedParent) && Boolean(selectedGroup);

    // Dropdown 1: parent suppliers (top-level), with the email-based suggestion
    // marked as a clue.
    const parentOptions = (suppliers ?? []).map((b) => ({
        value: b.supplierId,
        label: `${b.supplierName}${b.supplierId === suggestedId ? ' — suggested' : ''}`,
        description: `${b.entityCount} legal ${b.entityCount === 1 ? 'entity' : 'entities'
            }`,
    }));
    const selectedParentOption = parentOptions.find((o) => o.value === selectedParent) ?? null;

    // Dropdown 2: existing groups under the parent + "Create a new group".
    const groupOptions = [
        { value: NEW_GROUP, label: 'Create a new group' },
        ...(groups ?? []).map((g) => ({
            value: g.supplierId,
            label: g.supplierName,
            description: `${g.entityCount} legal ${g.entityCount === 1 ? 'entity' : 'entities'
                }`,
        })),
    ];
    const selectedGroupOption = groupOptions.find((o) => o.value === selectedGroup) ?? null;

    return (
        <Modal
            visible
            onDismiss={onCancel}
            header={headerText}
            size="large"
            footer={
                <Box float="right">
                    <SpaceBetween size="xs" direction="horizontal">
                        <Button variant="link" onClick={onCancel} disabled={submitting}>
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            onClick={confirm}
                            loading={submitting}
                            disabled={!canConfirm}
                        >
                            {confirmLabel}
                        </Button>
                    </SpaceBetween>
                </Box>
            }
        >
            <SpaceBetween size="m">
                <Box color="text-body-secondary">
                    Assign <strong>{email}</strong>. Pick the supplier, then a group — create a
                    new group (this login becomes its admin) or add the login to an existing
                    group as a user.
                </Box>
                {loadError ? <Alert type="error">{loadError}</Alert> : null}
                {suppliers === null && !loadError ? <Spinner /> : null}
                {suppliers !== null ? (
                    <FormField label="Supplier">
                        <Select
                            selectedOption={selectedParentOption}
                            options={parentOptions}
                            onChange={({ detail }) =>
                                setSelectedParent(detail.selectedOption?.value ?? '')
                            }
                        />
                    </FormField>
                ) : null}

                {selectedParent ? (
                    <FormField
                        label="Group"
                        description="Create a new group (this login becomes its admin and you pick its legal entities), or add the login to an existing group as an admin or user."
                    >
                        {groups === null ? (
                            <Spinner />
                        ) : (
                            <Select
                                selectedOption={selectedGroupOption}
                                options={groupOptions}
                                onChange={({ detail }) =>
                                    setSelectedGroup(detail.selectedOption?.value ?? NEW_GROUP)
                                }
                            />
                        )}
                    </FormField>
                ) : null}

                {/* For an EXISTING group, the approver picks the login's role in
                    that group. Admin adds a second master; a group may have
                    several masters. (A new group's owning login is always its
                    admin, so no choice is shown.) */}
                {selectedParent && !isNewGroup ? (
                    <FormField
                        label="Role in this group"
                        description="Admin can manage the group's users and shares the group's credential. User can submit and view. A group may have more than one admin."
                    >
                        <RadioGroup
                            value={existingRole}
                            onChange={({ detail }) =>
                                setExistingRole(detail.value as 'MASTER' | 'MEMBER')
                            }
                            items={[
                                {
                                    value: 'MASTER',
                                    label: 'Admin',
                                    description: 'Adds this login as another admin of the group.',
                                },
                                { value: 'MEMBER', label: 'User' },
                            ]}
                        />
                    </FormField>
                ) : null}

                {selectedParent ? (
                    <>
                        {isNewGroup ? (
                            <Alert type="info">
                                A new group will be created under{' '}
                                <strong>{selectedParentSupplier?.supplierName}</strong> and this
                                login becomes its admin. The group is named automatically and
                                covers the legal entities you select below. A supplier can have
                                several admins, each owning a separate group.
                            </Alert>
                        ) : (
                            <Alert type="info">
                                This login joins the selected group as{' '}
                                <strong>{existingRole === 'MASTER' ? 'an admin' : 'a user'}</strong>{' '}
                                and inherits its legal entities (shown below, not editable).
                                {existingRole === 'MASTER'
                                    ? ' The group will then have more than one admin.'
                                    : ''}
                            </Alert>
                        )}
                        <FormField
                            label="Legal entities for this group"
                            description={
                                isNewGroup
                                    ? "Select the legal entities this new group covers (a subset of the supplier's entities). Entities may be shared across groups."
                                    : "This group's entities, set by its admin. A user inherits them and they can't be changed here."
                            }
                        >
                            {entities === null ? <Spinner /> : null}
                            {entities !== null ? (
                                <Table
                                    variant="embedded"
                                    items={entities}
                                    resizableColumns
                                    selectionType={isNewGroup ? 'multi' : undefined}
                                    selectedItems={
                                        isNewGroup
                                            ? entities.filter((e) => checkedNos[e.supplierNo])
                                            : undefined
                                    }
                                    onSelectionChange={({ detail }) =>
                                        setCheckedNos(
                                            Object.fromEntries(
                                                detail.selectedItems.map((e) => [
                                                    e.supplierNo,
                                                    true,
                                                ]),
                                            ),
                                        )
                                    }
                                    trackBy="supplierNo"
                                    header={
                                        <Header
                                            counter={
                                                isNewGroup
                                                    ? `(${checkedList().length}/${entities.length})`
                                                    : `(${entities.length})`
                                            }
                                            variant="h3"
                                        >
                                            Legal entities
                                        </Header>
                                    }
                                    empty={
                                        <Box
                                            textAlign="center"
                                            color="text-body-secondary"
                                            padding="s"
                                        >
                                            {isNewGroup
                                                ? 'No legal entities loaded for this supplier yet.'
                                                : 'This group has no legal entities assigned.'}
                                        </Box>
                                    }
                                    columnDefinitions={[
                                        {
                                            id: 'supplierNo',
                                            header: 'SupplierNo',
                                            cell: (e) => (
                                                <Box fontWeight="bold">{e.supplierNo}</Box>
                                            ),
                                        },
                                        {
                                            id: 'label',
                                            header: 'Legal entity',
                                            cell: (e) => e.displayLabel,
                                        },
                                    ]}
                                />
                            ) : null}
                        </FormField>
                    </>
                ) : null}
            </SpaceBetween>
        </Modal>
    );
}

// ── Approval queue ────────────────────────────────────────────────────────────
function QueueView({
    requests,
    approvingId,
    onApprove,
}: {
    requests: ControlRequest[];
    approvingId: string | null;
    onApprove: (r: ControlRequest) => void;
}) {
    const [q, setQ] = useState('');
    const query = q.trim().toLowerCase();
    const shown = requests.filter(
        (r) =>
            query === '' ||
            r.email.toLowerCase().includes(query) ||
            r.supplier.toLowerCase().includes(query),
    );

    return (
        <Table
            variant="container"
            items={shown}
            filter={
                <TextFilter
                    filteringText={q}
                    filteringPlaceholder="Search by email or supplier…"
                    onChange={({ detail }) => setQ(detail.filteringText)}
                />
            }
            header={<Header>Approval queue</Header>}
            empty={
                <Box textAlign="center" color="text-body-secondary" padding="l">
                    No pending requests to show yet.
                </Box>
            }
            columnDefinitions={[
                {
                    id: 'supplier',
                    header: 'Supplier',
                    cell: (r) => <PartyCell name={r.supplier} email={r.email} seed={r.id} />,
                },

                {
                    id: 'status',
                    header: 'Status',
                    cell: (r) => {
                        // Onboarding rows (public passwordless flow) have no
                        // Cognito email verification — they're ready for the
                        // approver as soon as they arrive.
                        if (r.kind === 'onboarding') {
                            return (
                                <StatusIndicator type="pending">
                                    New supplier · Awaiting approval
                                </StatusIndicator>
                            );
                        }
                        return r.verified ? (
                            <StatusIndicator type="pending">
                                Email verified · Awaiting approval
                            </StatusIndicator>
                        ) : (
                            <StatusIndicator type="in-progress">
                                Awaiting email verification
                            </StatusIndicator>
                        );
                    },
                },
                { id: 'submitted', header: 'Submitted', cell: (r) => fmtDate(r.submitted) },
                {
                    id: 'actions',
                    header: '',
                    cell: (r) => {
                        // Approvable when the email is verified (sign-ups) OR it's
                        // an onboarding request (no verification step).
                        const ready = r.verified || r.kind === 'onboarding';
                        return ready ? (
                            <Button
                                variant="primary"
                                disabled={approvingId !== null}
                                loading={approvingId === r.id}
                                onClick={() => onApprove(r)}
                            >
                                Approve…
                            </Button>
                        ) : (
                            <Box color="text-body-secondary">Not ready</Box>
                        );
                    },
                },
            ]}
        />
    );
}

// ── Supplier review (per-site grant decisions for one pending login) ──────────
type Decision = 'pending' | 'approved' | 'rejected';

function ReviewView({
    request,
    sites,
    onBack,
}: {
    request: ControlRequest | null;
    sites: ControlSite[];
    onBack: () => void;
}) {
    const allTo = (d: Decision): Record<string, Decision> =>
        Object.fromEntries(sites.map((s) => [s.id, d]));
    const [decisions, setDecisions] = useState<Record<string, Decision>>(allTo('pending'));
    const [done, setDone] = useState(false);
    useEffect(() => setDecisions(Object.fromEntries(sites.map((s) => [s.id, 'pending']))), [sites]);

    const set = (id: string, d: Decision) => setDecisions((prev) => ({ ...prev, [id]: d }));
    const allDecided = sites.length > 0 && sites.every((s) => decisions[s.id] !== 'pending');
    const approvedCount = sites.filter((s) => decisions[s.id] === 'approved').length;

    if (!request) {
        return (
            <Container>
                <Box textAlign="center" padding="l">
                    <SpaceBetween size="s">
                        <Box color="text-body-secondary">No request selected.</Box>
                        <Button variant="link" onClick={onBack}>
                            Back to queue
                        </Button>
                    </SpaceBetween>
                </Box>
            </Container>
        );
    }

    if (done) {
        return (
            <Container>
                <Box textAlign="center" padding="xl">
                    <SpaceBetween size="m">
                        <StatusIndicator type="success">Decision recorded</StatusIndicator>
                        <Box color="text-body-secondary">
                            {request.email} was granted access to {approvedCount} of {sites.length}{' '}
                            site(s).
                        </Box>
                        <Button onClick={onBack}>Back to queue</Button>
                    </SpaceBetween>
                </Box>
            </Container>
        );
    }

    return (
        <SpaceBetween size="l">
            <Button variant="link" iconName="angle-left" onClick={onBack}>
                Back to queue
            </Button>

            <Container
                header={
                    <Header variant="h3" description={request.email}>
                        {request.supplier} <StatusIndicator type="pending">Pending login</StatusIndicator>
                    </Header>
                }
            >
                <SpaceBetween size="m">
                    <Box variant="awsui-key-label">
                        Grant site access · {approvedCount} of {sites.length} granted
                    </Box>
                    {sites.length === 0 ? (
                        <Box textAlign="center" color="text-body-secondary" padding="m">
                            No sites to show yet.
                        </Box>
                    ) : (
                        sites.map((s) => {
                            const d = decisions[s.id];
                            return (
                                <Box key={s.id} padding={{ vertical: 'xs' }}>
                                    <SpaceBetween
                                        size="m"
                                        direction="horizontal"
                                        alignItems="center"
                                    >
                                        <SpaceBetween size="xxxs">
                                            <SpaceBetween
                                                size="xs"
                                                direction="horizontal"
                                                alignItems="center"
                                            >
                                                <Box fontWeight="bold">{s.name}</Box>
                                                <Badge>{s.code}</Badge>
                                            </SpaceBetween>
                                            <Box color="text-body-secondary" fontSize="body-s">
                                                {s.location}
                                            </Box>
                                        </SpaceBetween>
                                        <SpaceBetween
                                            size="xs"
                                            direction="horizontal"
                                            alignItems="center"
                                        >
                                            {d !== 'pending' ? (
                                                <StatusIndicator
                                                    type={d === 'approved' ? 'success' : 'error'}
                                                >
                                                    {d === 'approved' ? 'Access: Yes' : 'Access: No'}
                                                </StatusIndicator>
                                            ) : null}
                                            <Button
                                                variant={d === 'approved' ? 'primary' : 'normal'}
                                                onClick={() => set(s.id, 'approved')}
                                            >
                                                Yes
                                            </Button>
                                            <Button
                                                variant={d === 'rejected' ? 'primary' : 'normal'}
                                                onClick={() => set(s.id, 'rejected')}
                                            >
                                                No
                                            </Button>
                                        </SpaceBetween>
                                    </SpaceBetween>
                                </Box>
                            );
                        })
                    )}
                    {sites.length > 0 ? (
                        <SpaceBetween size="xs" direction="horizontal">
                            <Button onClick={() => setDecisions(allTo('approved'))}>
                                Approve all
                            </Button>
                            <Button onClick={() => setDecisions(allTo('rejected'))}>
                                Reject all
                            </Button>
                        </SpaceBetween>
                    ) : null}
                </SpaceBetween>
            </Container>

            <Box textAlign="right">
                <Button
                    variant="primary"
                    disabled={!allDecided}
                    onClick={() => allDecided && setDone(true)}
                >
                    {allDecided ? 'Confirm decisions' : 'Decide all sites first'}
                </Button>
            </Box>
        </SpaceBetween>
    );
}

// ── Users (ops view: supplier vs employee) ───────────────────────────────────
const USER_TYPE_FILTERS: { id: UserType | 'all'; text: string }[] = [
    { id: 'all', text: 'All' },
    { id: 'supplier', text: 'Suppliers' },
    { id: 'employee', text: 'Employees' },
];

function UsersView({
    users,
    onMove,
    movingUsername,
}: {
    users: ControlUser[];
    onMove: (u: ControlUser) => void;
    movingUsername: string | null;
}) {
    const [q, setQ] = useState('');
    const [typeFilter, setTypeFilter] = useState<UserType | 'all'>('all');
    const [roleFilter, setRoleFilter] = useState<'all' | 'MASTER' | 'MEMBER'>('all');

    // A supplier's effective brand role: no stamped role means MEMBER.
    const effectiveRole = (u: ControlUser): string =>
        u.type === 'supplier' ? (u.role || 'MEMBER').toUpperCase() : '';

    const query = q.trim().toLowerCase();
    const shown = users.filter(
        (u) =>
            (typeFilter === 'all' || u.type === typeFilter) &&
            // Role filter applies to suppliers only (employees have no brand role).
            (roleFilter === 'all' ||
                (u.type === 'supplier' && effectiveRole(u) === roleFilter)) &&
            (query === '' ||
                u.email.toLowerCase().includes(query) ||
                u.username.toLowerCase().includes(query)),
    );

    const ROLE_FILTER_OPTIONS = [
        { value: 'all', label: 'All roles' },
        { value: 'MASTER', label: 'Master' },
        { value: 'MEMBER', label: 'Member' },
    ];
    const selectedRoleOption =
        ROLE_FILTER_OPTIONS.find((o) => o.value === roleFilter) ?? ROLE_FILTER_OPTIONS[0];

    const supplierCount = users.filter((u) => u.type === 'supplier').length;
    const employeeCount = users.filter((u) => u.type === 'employee').length;

    return (
        <Table
            variant="container"
            items={shown}
            filter={
                <SpaceBetween size="s">
                    <SpaceBetween size="xs" direction="horizontal">
                        <Badge color="blue">{`${supplierCount} suppliers`}</Badge>
                        <Badge color="grey">{`${employeeCount} employees`}</Badge>
                    </SpaceBetween>
                    <SpaceBetween size="s" direction="horizontal" alignItems="center">
                        <SegmentedControl
                            selectedId={typeFilter}
                            onChange={({ detail }) =>
                                setTypeFilter(detail.selectedId as UserType | 'all')
                            }
                            options={USER_TYPE_FILTERS}
                        />
                        <Select
                            selectedOption={selectedRoleOption}
                            onChange={({ detail }) =>
                                setRoleFilter(
                                    (detail.selectedOption?.value as
                                        | 'all'
                                        | 'MASTER'
                                        | 'MEMBER') ?? 'all',
                                )
                            }
                            options={ROLE_FILTER_OPTIONS}
                        />
                        <TextFilter
                            filteringText={q}
                            filteringPlaceholder="Search by email or username…"
                            onChange={({ detail }) => setQ(detail.filteringText)}
                        />
                    </SpaceBetween>
                </SpaceBetween>
            }
            header={<Header>Users</Header>}
            empty={
                <Box textAlign="center" color="text-body-secondary" padding="l">
                    No users to show yet.
                </Box>
            }
            columnDefinitions={[
                {
                    id: 'user',
                    header: 'User',
                    cell: (u) => (
                        <SpaceBetween size="xxxs">
                            <Box fontWeight="bold">{u.email}</Box>
                            <Box color="text-body-secondary" fontSize="body-s">
                                {u.username}
                            </Box>
                        </SpaceBetween>
                    ),
                },
                {
                    id: 'type',
                    header: 'Type',
                    cell: (u) => (
                        <Badge color={u.type === 'employee' ? 'grey' : 'blue'}>
                            {u.type === 'employee' ? 'Employee' : 'Supplier'}
                        </Badge>
                    ),
                },

                {
                    id: 'supplier',
                    header: 'Supplier',
                    cell: (u) => {
                        if (u.type === 'supplier' && u.supplierId) {
                            return <Badge color="green">{u.supplierId}</Badge>;
                        }
                        return <Box color="text-body-secondary">—</Box>;
                    },
                },
                {
                    id: 'supplierRole',
                    header: 'Brand role',
                    cell: (u) => {
                        // Only suppliers have a brand role; a supplier with no
                        // stamped role is treated as a member.
                        if (u.type !== 'supplier') {
                            return <Box color="text-body-secondary">—</Box>;
                        }
                        const role = (u.role || 'MEMBER').toUpperCase();
                        return (
                            <Badge color={role === 'MASTER' ? 'green' : 'grey'}>{role}</Badge>
                        );
                    },
                },
                {
                    id: 'status',
                    header: 'Status',
                    cell: (u) => (
                        <StatusIndicator type={u.enabled ? 'success' : 'stopped'}>
                            {u.enabled ? u.status : 'Disabled'}
                        </StatusIndicator>
                    ),
                },
                { id: 'created', header: 'Created', cell: (u) => fmtDate(u.created) },
                {
                    id: 'actions',
                    header: '',
                    // Only approved suppliers can be moved between teams.
                    cell: (u) =>
                        u.type === 'supplier' && u.enabled ? (
                            <Button
                                variant="normal"
                                disabled={movingUsername !== null}
                                loading={movingUsername === u.username}
                                onClick={() => onMove(u)}
                            >
                                Change supplier
                            </Button>
                        ) : null,
                },
            ]}
        />
    );
}

// ── Member requests (master-requested teammates awaiting approval) ────────────
function MemberRequestsView({
    requests,
    busyId,
    onApprove,
    onReject,
}: {
    requests: ControlMemberRequest[];
    busyId: string | null;
    onApprove: (r: ControlMemberRequest) => void;
    onReject: (r: ControlMemberRequest) => void;
}) {
    return (
        <Table
            variant="container"
            items={requests}
            header={
                <Header counter={`(${requests.length})`}>Member requests</Header>
            }
            empty={
                <Box textAlign="center" color="text-body-secondary" padding="l">
                    No pending member requests.
                </Box>
            }
            columnDefinitions={[
                {
                    id: 'email',
                    header: 'User',
                    cell: (r) => <Box fontWeight="bold">{r.email}</Box>,
                },
                {
                    id: 'group',
                    header: 'Group',
                    cell: (r) => r.targetSupplierId,
                },
                {
                    id: 'requestedBy',
                    header: 'Requested by',
                    cell: (r) => r.requestedByEmail || r.requestedByMaster || '—',
                },
                {
                    id: 'actions',
                    header: '',
                    cell: (r) => (
                        <SpaceBetween size="xs" direction="horizontal">
                            <Button
                                variant="primary"
                                loading={busyId === r.requestId}
                                disabled={busyId !== null}
                                onClick={() => onApprove(r)}
                            >
                                Approve
                            </Button>
                            <Button
                                variant="normal"
                                disabled={busyId !== null}
                                onClick={() => onReject(r)}
                            >
                                Reject
                            </Button>
                        </SpaceBetween>
                    ),
                },
            ]}
        />
    );
}

// ── Main Control area ─────────────────────────────────────────────────────────
export default function SupplierControlScreen() {
    const [view, setView] = useState<ControlView>('suppliers');
    const [suppliers, setSuppliers] = useState<OnboardedSupplier[]>([]);
    const [requests, setRequests] = useState<ControlRequest[]>([]);
    const [users, setUsers] = useState<ControlUser[]>([]);
    const [memberRequests, setMemberRequests] = useState<ControlMemberRequest[]>([]);
    const [memberBusyId, setMemberBusyId] = useState<string | null>(null);
    const [memberError, setMemberError] = useState<string | null>(null);
    const [reviewId, setReviewId] = useState<string | null>(null);
    const [approvingId, setApprovingId] = useState<string | null>(null);
    const [approveError, setApproveError] = useState<string | null>(null);
    // The pending request currently being assigned a team in the picker modal.
    const [pickFor, setPickFor] = useState<ControlRequest | null>(null);
    // The approved user currently being moved to another team.
    const [moveUser, setMoveUser] = useState<ControlUser | null>(null);
    const [movingUsername, setMovingUsername] = useState<string | null>(null);

    const confirmReassign = (assignment: SupplierAssignment) => {
        if (!moveUser) return;
        const { username } = moveUser;
        setMovingUsername(username);
        getSupplierControlService()
            .reassignSupplier(username, assignment)
            .then(() => {
                setMoveUser(null);
                // Refresh the users list so the new team shows.
                return getSupplierControlService().listUsers().then(setUsers);
            })
            .finally(() => setMovingUsername(null));
    };

    const confirmApprove = (assignment: SupplierAssignment) => {
        if (!pickFor) return;
        const { id } = pickFor;
        setApprovingId(id);
        setApproveError(null);
        getSupplierControlService()
            .approveRequest(id, assignment)
            .then((refreshed) => {
                setRequests(refreshed);
                setPickFor(null);
            })
            .catch((e) =>
                setApproveError(e instanceof Error ? e.message : 'Failed to approve request.'),
            )
            .finally(() => setApprovingId(null));
    };

    const approveMember = (r: ControlMemberRequest) => {
        setMemberBusyId(r.requestId);
        setMemberError(null);
        getSupplierControlService()
            .approveMemberRequest(r.requestId)
            .then(setMemberRequests)
            .catch((e) =>
                setMemberError(e instanceof Error ? e.message : 'Failed to approve request.'),
            )
            .finally(() => setMemberBusyId(null));
    };

    const rejectMember = (r: ControlMemberRequest) => {
        setMemberBusyId(r.requestId);
        setMemberError(null);
        getSupplierControlService()
            .rejectMemberRequest(r.requestId)
            .then(setMemberRequests)
            .catch((e) =>
                setMemberError(e instanceof Error ? e.message : 'Failed to reject request.'),
            )
            .finally(() => setMemberBusyId(null));
    };

    useEffect(() => {
        let live = true;
        const svc = getSupplierControlService();
        Promise.all([
            svc.listOnboardedSuppliers(),
            svc.listRequests(),
            svc.listUsers(),
            svc.listMemberRequests(),
        ])
            .then(([s, r, u, mr]) => {
                if (live) {
                    setSuppliers(s);
                    setRequests(r);
                    setUsers(u);
                    setMemberRequests(mr);
                }
            })
            .catch(() => {
                if (live) {
                    setSuppliers([]);
                    setRequests([]);
                    setUsers([]);
                    setMemberRequests([]);
                }
            });
        return () => {
            live = false;
        };
    }, []);

    const reviewRequest = useMemo(
        () => requests.find((r) => r.id === reviewId) ?? null,
        [requests, reviewId],
    );
    // Per-site review needs the sites model, which is a separate follow-up; no
    // sites are available yet, so the review screen shows its empty state.
    const reviewSites: ControlSite[] = useMemo(() => [], []);

    const pendingCount = requests.filter((r) => r.verified).length;

    // The review sub-view is reached from the queue; it isn't its own tab.
    if (view === 'review') {
        return (
            <ReviewView
                request={reviewRequest}
                sites={reviewSites}
                onBack={() => {
                    setView('queue');
                    setReviewId(null);
                }}
            />
        );
    }

    const tabs = (
        <Tabs
            activeTabId={view}
            onChange={({ detail }) => {
                setView(detail.activeTabId as ControlView);
                setReviewId(null);
            }}
            tabs={[
                {
                    id: 'suppliers',
                    label: 'Onboarded Suppliers',
                    content: <SuppliersView suppliers={suppliers} />,
                },
                {
                    id: 'queue',
                    label: pendingCount > 0 ? `Approval Queue (${pendingCount})` : 'Approval Queue',
                    content: (
                        <SpaceBetween size="s">
                            {approveError ? <Alert type="error">{approveError}</Alert> : null}
                            <QueueView
                                requests={requests}
                                approvingId={approvingId}
                                onApprove={setPickFor}
                            />
                        </SpaceBetween>
                    ),
                },
                {
                    id: 'member-requests',
                    label:
                        memberRequests.length > 0
                            ? `Member Requests (${memberRequests.length})`
                            : 'Member Requests',
                    content: (
                        <SpaceBetween size="s">
                            {memberError ? <Alert type="error">{memberError}</Alert> : null}
                            <MemberRequestsView
                                requests={memberRequests}
                                busyId={memberBusyId}
                                onApprove={approveMember}
                                onReject={rejectMember}
                            />
                        </SpaceBetween>
                    ),
                },
                {
                    id: 'users',
                    label: 'Users',
                    content: (
                        <UsersView
                            users={users}
                            onMove={setMoveUser}
                            movingUsername={movingUsername}
                        />
                    ),
                },
            ]}
        />
    );

    return (
        <>
            {tabs}
            {pickFor ? (
                <SupplierPickerModal
                    headerText={`Assign a supplier for ${pickFor.email}`}
                    confirmLabel="Approve"
                    email={pickFor.email}
                    submitting={approvingId === pickFor.id}
                    onCancel={() => (approvingId ? undefined : setPickFor(null))}
                    onConfirm={confirmApprove}
                />
            ) : null}
            {moveUser ? (
                <SupplierPickerModal
                    headerText={`Change supplier for ${moveUser.email}`}
                    confirmLabel="Move"
                    email={moveUser.email}
                    submitting={movingUsername === moveUser.username}
                    onCancel={() => (movingUsername ? undefined : setMoveUser(null))}
                    onConfirm={confirmReassign}
                />
            ) : null}
        </>
    );
}
